import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, CalendarDays, Users, ListOrdered, Mail, ClipboardCheck, FileSignature, Loader2, Plus } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { useDocumentPreview } from '../components/DocumentPreviewModal';
import { useConseil } from '../components/conseil/useConseil';
import { EnteteConseil } from '../components/conseil/EnteteConseil';
import { OngletSeance } from '../components/conseil/OngletSeance';
import { OngletParticipants } from '../components/conseil/OngletParticipants';
import { OngletOrdreDuJour } from '../components/conseil/OngletOrdreDuJour';
import { OngletConvocations } from '../components/conseil/OngletConvocations';
import { OngletPresences } from '../components/conseil/OngletPresences';
import { OngletCompteRendu } from '../components/conseil/OngletCompteRendu';
import { PVValides } from '../components/conseil/PVValides';

const ONGLETS = [['seance', 'Séance', CalendarDays, OngletSeance], ['participants', 'Participants', Users, OngletParticipants],
  ['odj', 'Ordre du jour', ListOrdered, OngletOrdreDuJour], ['convocations', 'Convocations', Mail, OngletConvocations],
  ['presences', 'Présences', ClipboardCheck, OngletPresences], ['cr', 'Compte rendu', FileSignature, OngletCompteRendu]];

const CreerConseil = ({ annee, onCreer }) => (
  <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="cp-creer-bloc">
    <p className="text-base md:text-lg font-semibold text-[#0F172A]">Pas encore de conseil de perfectionnement pour {annee}</p>
    <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">Créez-le pour préparer la séance, l'ordre du jour et les convocations.</p>
    <Button onClick={onCreer} className="mt-6 rounded-full bg-[#0F172A] px-6" data-testid="cp-creer"><Plus className="mr-2 h-4 w-4" /> Créer le conseil {annee}</Button>
  </div>
);

export default function ConseilPerfectionnement() {
  const actuelle = new Date().getFullYear();
  const [annee, setAnnee] = useState(actuelle);
  const [onglet, setOnglet] = useState('seance');
  const [liste, setListe] = useState([]);
  const cp = useConseil(annee);
  const { conseil } = cp;
  const { previewDocument, DocumentModal } = useDocumentPreview();
  useEffect(() => {
    axios.get('/conseil-perfectionnement').then(({ data }) => setListe(data)).catch(() => setListe([]));
  }, [conseil?.annee, conseil?.statut]);
  const annees = [...new Set([actuelle + 1, actuelle, actuelle - 1, actuelle - 2, ...liste.map((c) => c.annee)])].sort((a, b) => b - a);
  const creer = () => axios.post('/conseil-perfectionnement', { annee })
    .then(() => { toast.success(`Conseil de perfectionnement ${annee} créé`); cp.recharger(); })
    .catch((e) => toast.error(e.response?.data?.detail || 'Création impossible'));

  return (
    <div className="space-y-6" data-testid="cp-page">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 transition-colors hover:text-[#0F172A]" data-testid="cp-retour">
        <ArrowLeft className="h-4 w-4" /> Tableau de bord
      </Link>
      <EnteteConseil annee={annee} annees={annees} onAnnee={(a) => { setAnnee(a); setOnglet('seance'); }} conseil={conseil}
        onBilan={() => previewDocument(null, `Bilan d'activité ${annee}`, `/conseil-perfectionnement/${annee}/bilan-activite.pdf`)} onPdf={previewDocument} />
      {conseil === undefined && <div className="flex justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>}
      {conseil === false && <p className="text-red-600" data-testid="cp-erreur">Impossible de charger le conseil de perfectionnement.</p>}
      {conseil === null && <CreerConseil annee={annee} onCreer={creer} />}
      {conseil && (
        <Tabs value={onglet} onValueChange={setOnglet}>
          <TabsList className="h-auto w-full flex-wrap justify-start gap-1 bg-slate-100 p-1 sm:w-auto">
            {ONGLETS.map(([k, l, Icone], i) => (
              <TabsTrigger key={k} value={k} data-testid={`cp-onglet-${k}`} className="gap-1.5">
                <span className="hidden text-[10px] font-bold text-slate-400 sm:inline">{i + 1}</span><Icone className="h-4 w-4" /> {l}
              </TabsTrigger>
            ))}
          </TabsList>
          {ONGLETS.map(([k, , , Onglet]) => (
            <TabsContent key={k} value={k} className="mt-5"><Onglet cp={cp} onPdf={previewDocument} /></TabsContent>
          ))}
        </Tabs>
      )}
      <PVValides liste={liste} onPdf={previewDocument} />
      <DocumentModal />
    </div>
  );
}
